import React, { useState } from 'react';
import Image from 'next/image';
import UseTranslation from 'next-translate/useTranslation';
import Icon from '@lushdigital/icons';
import clsx from 'clsx';

type AvatarProps = {
  profile_image?: string;
  first_name?: string;
  last_name?: string;
  size?: 'small' | 'large';
  className?: string;
  showNameOnHover?: boolean;
};

const Avatar = ({ profile_image, first_name, last_name, size, className, showNameOnHover = true }: AvatarProps) => {
  const { t } = UseTranslation('tills');
  const [imageError, setImageError] = useState<boolean>(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const name = [first_name, last_name].filter(Boolean).join(' ');
  const initials = [first_name, last_name]
    .filter(Boolean)
    .map((s) => s?.charAt(0))
    .join('');
  const altText = t(name ? 'AVATAR_ALT_TEXT' : 'AVATAR_ALT_TEXT_WITHOUT_USER_NAME', { name });

  const sizeStyles = {
    small: { size: { width: 32, height: 32 }, classes: 'h-7 w-7 text-xs' },
    large: { size: { width: 64, height: 64 }, classes: 'h-14 w-14 text-lg' },
    default: { size: { width: 42, height: 42 }, classes: 'h-12 w-12 text-sm' }
  };
  const { size: avatarSize, classes: sizeClasses } = sizeStyles[size || 'default'];

  const avatarClasses = clsx(
    'text-lush-black-88 border-lush-black-88 flex items-center justify-center rounded-full border bg-white font-semibold tracking-wider transition-colors duration-200 hover:bg-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-500 focus:ring-offset-2',
    sizeClasses,
    className
  );

  return (
    <div className="group relative">
      {(() => {
        if (profile_image && !imageError) {
          return (
            <Image
              src={profile_image}
              {...avatarSize}
              alt={altText}
              className="rounded-full"
              unoptimized
              onError={handleImageError}
            />
          );
        }
        if (initials) {
          return <div className={avatarClasses}>{initials}</div>;
        }
        return (
          <div className={avatarClasses} data-testid="avatar-profile-icon">
            <Icon icon="profile" size="1em" color="#8F8F8F" />
          </div>
        );
      })()}

      {name && showNameOnHover && (
        <div className="absolute top-full z-50 mt-1 hidden group-hover:block">
          <div className="whitespace-nowrap rounded bg-black px-2 py-1 text-xs text-white">{name}</div>
        </div>
      )}
    </div>
  );
};

export default Avatar;
